/**
 * Product Form
 *
 * - Syncs option radio changes to the hidden select[name="id"] (variant ID).
 * - Disables the submit button when the selected variant is unavailable.
 * - AJAX-submits the form and fires the same cart events as quick-add.js:
 *     cart:count:updated  { count, total }  → badge + shipping progress
 *     cart:item:added                        → cart panel opens + refreshes
 */

import { addWorking, removeWorking } from './utils.js';
import { applyShippingProgress } from './shipping-progress.js';

const form = document.querySelector('.product__form form');
if (!form) throw new Error('Product form not found');

const variantDataEl = document.getElementById('product-variants-data');
if (!variantDataEl) throw new Error('product-variants-data element not found');

const variants = JSON.parse(variantDataEl.textContent);
const optionContainer = form.querySelector('.product__options');
const variantSelect = form.querySelector('select[name="id"]');
const submitButton = form.querySelector('[type="submit"]');
const purchaseOptions = form.querySelector('purchase-options');

// ---------------------------------------------------------------------------
// Cart total tracking — kept fresh so previews are always based on reality
// ---------------------------------------------------------------------------

let currentCartTotal = 0;

// Seed from the live cart on page load (fire-and-forget)
fetch('/cart.js')
    .then(r => r.json())
    .then(cart => { currentCartTotal = cart.total_price; })
    .catch(() => { });

// Stay in sync after any cart mutation elsewhere on the page
document.addEventListener('cart:count:updated', e => {
    currentCartTotal = e.detail.total;
});

// ---------------------------------------------------------------------------
// Variant resolution — mirrors getSelectedVariant() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * Find the variant matching the currently checked option radios.
 * @returns {Object|null} Shopify variant object, or null if no match
 */
function getSelectedVariant() {
    const fieldsets = [...optionContainer.querySelectorAll('[data-option-position]')];
    // if we don't have fieldsets, then this product has no options and only one variant — return it
    if (fieldsets.length === 0) return variants[0] || null;
    const selectedOptions = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
    });

    if (selectedOptions.some(o => o === null)) return null;

    return variants.find(variant =>
        variant.options.every((option, index) => option === selectedOptions[index])
    ) || null;
}

// ---------------------------------------------------------------------------
// Update select + submit state after option change
// ---------------------------------------------------------------------------

/** Sync the hidden variant select and submit button to the currently checked options. */
function updateVariantState() {
    const variant = getSelectedVariant();

    if (variant) {
        variantSelect.value = variant.id;
        submitButton.disabled = !variant.available;
        submitButton.value = variant.available ? 'Add to cart' : 'Sold out';
    } else {
        variantSelect.value = '';
        submitButton.disabled = true;
        submitButton.value = 'Unavailable';
    }

    // Stay AI watches changes on the window, so append the variant ID to the URL and trigger a popstate
    if (purchaseOptions) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant ? variant.id : '');
        window.history.replaceState({}, '', url);
        // replaceState doesn't fire popstate — dispatch it manually
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    }
}

// ---------------------------------------------------------------------------
// Preview shipping progress — scoped to the form's own progress bar only,
// so the cart panel bar is never affected.
// ---------------------------------------------------------------------------

/** Preview free shipping progress based on the current selection and live cart total. */
function previewShippingProgress() {
    const progressEl = form.querySelector('.shipping-progress');
    if (!progressEl) return;

    const variant = getSelectedVariant();
    if (!variant) return;

    const quantity = parseInt(form.querySelector('.quantity-input__value')?.value || 1, 10);

    // Use subscription price when that purchase type is active
    let price = variant.price;
    const purchaseType = form.querySelector('[name="purchaseType"]:checked')?.value;
    if (purchaseType === 'purchaseTypeSubscription') {
        const sellingPlanId = parseInt(form.querySelector('[name="stayai_selling_plan"]')?.value, 10);
        if (sellingPlanId && variant.selling_plan_allocations?.length) {
            const allocation = variant.selling_plan_allocations.find(
                a => a.selling_plan_id === sellingPlanId
            );
            if (allocation) price = allocation.price;
        }
    }

    const previewTotal = currentCartTotal + price * quantity;
    applyShippingProgress(progressEl, previewTotal, true);
}

// ---------------------------------------------------------------------------
// Add to cart — mirrors addToCart() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * POST the selected variant to the Cart API and fire cart update events.
 * @param {number|string} variantId - Shopify variant ID
 */
async function addToCart(variantId) {
    addWorking(form);
    const requestBody = {
        id: variantId,
        quantity: parseInt(form.querySelector('[name="quantity"]')?.value || 1)
    };
    const purchaseType = form.querySelector('[name="purchaseType"]:checked')?.value;
    if (purchaseType === 'purchaseTypeSubscription') {
        requestBody.selling_plan = form.querySelector('[name="stayai_selling_plan"]')?.value;
    }
    try {
        const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });
        if (!res.ok) throw new Error('Cart add failed');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:count:updated', {
            detail: { count: cart.item_count, total: cart.total_price }
        }));
        document.dispatchEvent(new CustomEvent('cart:item:added'));
    } finally {
        removeWorking(form);
    }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

form.addEventListener('click', e => {
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (!qtyBtn) return;
    e.preventDefault();
    const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
    const qtyInput = form.querySelector('.quantity-input__value');
    if (!qtyInput) return;
    const current = parseInt(qtyInput.value, 10) || 1;
    qtyInput.value = Math.max(1, current + delta);
    previewShippingProgress();
});

// Live qty typing
form.addEventListener('input', e => {
    if (e.target.matches('.quantity-input__value')) previewShippingProgress();
});

// Variant radios, purchaseType, stayai_selling_plan select
form.addEventListener('change', previewShippingProgress);

optionContainer.addEventListener('change', updateVariantState);

form.addEventListener('submit', async e => {
    e.preventDefault();
    const variantId = variantSelect.value;
    if (!variantId) return;
    await addToCart(variantId);
});

// Set initial state on load (Liquid pre-selects checked radios, so this
// ensures the select and button reflect the server-rendered selection)
updateVariantState();
